/**
 * Structured Data (JSON-LD) for SEO & AEO (Answer Engine Optimization)
 * Supports: FAQPage, Article, Course, JobPosting, FinancialProduct, WebSite, Organization
 */

export function FAQSchema({ faqs = [] }) {
  if (!faqs.length) return null;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}

export function ArticleSchema({ title, description, datePublished, author, url }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "datePublished": datePublished,
    "author": { "@type": "Organization", "name": author || "YourMoneyWise" },
    "publisher": { "@type": "Organization", "name": "YourMoneyWise", "url": "https://yourmoneywise.net" },
    ...(url ? { "mainEntityOfPage": url } : {}),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}

export function CourseSchema({ name, description, provider, url, isFree }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": name,
    "description": description,
    "provider": { "@type": "Organization", "name": provider },
    ...(url ? { "url": url } : {}),
    "isAccessibleForFree": !!isFree,
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}

export function JobPostingSchema({ title, company, location, description, datePosted, salary }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": title,
    "hiringOrganization": { "@type": "Organization", "name": company },
    "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressLocality": location, "addressCountry": "IN" } },
    "description": description,
    "datePosted": datePosted,
    ...(salary ? { "baseSalary": { "@type": "MonetaryAmount", "currency": "INR", "value": { "@type": "QuantitativeValue", "value": salary } } } : {}),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}

export function WebSiteSchema() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "YourMoneyWise",
    "url": "https://yourmoneywise.net",
    "description": "India's trusted platform for financial calculators, product comparisons, learning modules, and career resources.",
    "publisher": {
      "@type": "Organization",
      "name": "YourMoneyWise",
      "url": "https://yourmoneywise.net",
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://yourmoneywise.net/finance/blog?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}

export function FinancialCalculatorSchema({ name, description, url }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "applicationCategory": "FinanceApplication",
    "description": description,
    "operatingSystem": "Web",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
    ...(url ? { "url": url } : {}),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}
